import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Trophy } from 'lucide-react';
import { academyInternalApi } from './apiInternal';

/**
 * Dialog admin pour saisir la grille pratique après quiz réussi.
 * Props: open, onClose, enrollment {id, training_id, user_name, training?.title}, onValidated()
 */
const AcademyPracticalEvalDialog = ({ open, onClose, enrollment, onValidated }) => {
  const [criteria, setCriteria] = useState([]);
  const [totalMax, setTotalMax] = useState(20);
  const [passing, setPassing] = useState(16);
  const [scores, setScores] = useState({}); // {crit_id: points}
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !enrollment) return;
    setLoading(true);
    academyInternalApi.fullContent(enrollment.training_id)
      .then((full) => {
        setCriteria(full.practical.criteria);
        setTotalMax(full.practical.total_max);
        setPassing(full.practical.passing);
        // init scores at 0
        const init = {};
        full.practical.criteria.forEach((c) => { init[c.id] = 0; });
        setScores(init);
      })
      .catch(() => toast.error("Erreur de chargement des critères."))
      .finally(() => setLoading(false));
  }, [open, enrollment]);

  const total = Object.values(scores).reduce((s, v) => s + (Number(v) || 0), 0);
  const passingScaled = Math.round(totalMax * passing / 20);
  const willPass = total >= passingScaled;

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = Object.entries(scores).map(([criterion_id, points]) => ({
        criterion_id, points: Number(points) || 0,
      }));
      const res = await academyInternalApi.submitPracticalEval(enrollment.id, payload, comment);
      if (res.practical_eval?.passed) {
        toast.success(`Évaluation validée · Certificat ${res.certificate?.number} généré.`);
      } else {
        toast.warning(`Note ${res.practical_eval.total}/${res.practical_eval.total_max} — inférieure au seuil (${passingScaled}). L'agent peut être réévalué.`);
      }
      if (onValidated) onValidated();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="academy-practical-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-emerald-600" />
            Évaluation pratique
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
            <div className="font-medium">{enrollment?.user_name}</div>
            <div className="text-xs text-slate-600">{enrollment?.training?.title}</div>
            <div className="text-xs mt-1">Seuil requis : <strong>{passingScaled}/{totalMax}</strong> (≥ 16/20 sur l&apos;échelle documentaire)</div>
          </div>

          {loading ? (
            <div className="text-center py-6 text-slate-500">Chargement…</div>
          ) : (
            <div className="space-y-2">
              {criteria.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800">{c.label}</div>
                    <div className="text-xs text-slate-500">Sur {c.max_points} pts</div>
                  </div>
                  <Input
                    type="number"
                    min="0"
                    max={c.max_points}
                    value={scores[c.id] ?? 0}
                    onChange={(e) => {
                      const v = Math.max(0, Math.min(c.max_points, Number(e.target.value) || 0));
                      setScores({ ...scores, [c.id]: v });
                    }}
                    className="w-24"
                    data-testid={`academy-practical-input-${c.id}`}
                  />
                </div>
              ))}
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-900 text-white">
                <div className="font-medium">Total</div>
                <div className="text-xl font-bold flex items-center gap-3">
                  {total} / {totalMax}
                  <Badge className={willPass ? 'bg-emerald-500' : 'bg-red-500'}>
                    {willPass ? 'Validation' : 'Non validé'}
                  </Badge>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="text-sm text-slate-700 font-medium">Commentaire (optionnel)</label>
            <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Observations du valideur…" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={submitting || loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="academy-practical-submit">
            {submitting ? 'Envoi…' : willPass ? 'Valider et générer le certificat' : 'Enregistrer (non validé)'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AcademyPracticalEvalDialog;
